/**
 * useTickerSearch - Debounced ticker symbol search via FMP API.
 *
 * Provides autocomplete results for ticker search inputs with:
 * - 300ms debounce to minimize API calls
 * - In-memory cache for repeated queries
 * - Loading and error state management
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '@/lib/api';

export interface TickerSearchResult {
  symbol: string;
  name: string;
  exchange: string;
}

interface UseTickerSearchReturn {
  results: TickerSearchResult[];
  isLoading: boolean;
  error: string | null;
  search: (query: string) => void;
  clear: () => void;
}

const cache = new Map<string, TickerSearchResult[]>();

export function useTickerSearch(): UseTickerSearchReturn {
  const [results, setResults] = useState<TickerSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const search = useCallback((query: string) => {
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Clear previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const trimmed = query.trim();
    if (trimmed.length < 1) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    // Check cache
    const cacheKey = trimmed.toUpperCase();
    if (cache.has(cacheKey)) {
      setResults(cache.get(cacheKey)!);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const token = localStorage.getItem('access_token');
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(
          `${API_BASE_URL}/api/v1/financial/search?q=${encodeURIComponent(trimmed)}`,
          { signal: controller.signal, headers }
        );

        if (!response.ok) {
          throw new Error('Search failed');
        }

        const data = await response.json();
        const searchResults: TickerSearchResult[] = data.results || [];

        // Cache the results
        cache.set(cacheKey, searchResults);

        setResults(searchResults);
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return; // Ignore aborted requests
        }
        setError('Failed to search tickers');
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);
  }, []);

  const clear = useCallback(() => {
    setResults([]);
    setError(null);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return { results, isLoading, error, search, clear };
}
